import { DataTableActionBarAction } from '@/components/data-table/data-table-action-bar'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogHeader,
	DialogFooter,
	DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useBulkUpdateQuantity } from '@/lib/api/subscriptions'
import { Hash, Loader2 } from 'lucide-react'
import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

interface UpdateQuantityBulkActionModalProps {
	subscriptionIds: string[]
	size?: 'sm' | 'md'
}

export const UpdateQuantityBulkActionModal = ({
	subscriptionIds,
	size = 'sm',
}: UpdateQuantityBulkActionModalProps) => {
	const { t } = useTranslation('subscriptions')
	const { mutate: updateQuantityMutation, isPending } = useBulkUpdateQuantity()
	const [isOpen, setIsOpen] = useState(false)
	const [quantityInput, setQuantityInput] = useState('')

	const isValid = quantityInput !== '' && Number(quantityInput) >= 1 && Number.isInteger(Number(quantityInput))

	const handleOpen = useCallback(() => {
		setQuantityInput('')
		setIsOpen(true)
	}, [])

	const handleSubmit = useCallback(() => {
		if (!isValid) return
		updateQuantityMutation(
			{ subscriptionIds, quantity: Number(quantityInput) },
			{ onSettled: () => setIsOpen(false) },
		)
	}, [isValid, updateQuantityMutation, subscriptionIds, quantityInput])

	const handleOpenChange = (open: boolean) => {
		if (!open && !isPending) setIsOpen(false)
	}

	return (
		<>
			{size === 'sm' && (
				<DataTableActionBarAction
					tooltip={t('bulkActions.updateQuantity.tooltip')}
					isPending={isPending}
					onClick={handleOpen}
				>
					<Hash className="mr-2 h-4 w-4" />
					{t('bulkActions.updateQuantity.button')}
				</DataTableActionBarAction>
			)}
			{size === 'md' && (
				<Button variant="outline" size="sm" onClick={handleOpen}>
					<Hash className="mr-2 h-4 w-4" />
					{t('bulkActions.updateQuantity.button')}
				</Button>
			)}
			<Dialog open={isOpen} onOpenChange={handleOpenChange}>
				<DialogContent className="sm:max-w-[420px]">
					<DialogHeader>
						<DialogTitle>{t('bulkActions.updateQuantity.title')}</DialogTitle>
						<DialogDescription>
							{t('bulkActions.updateQuantity.confirmMessage', { count: subscriptionIds.length })}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-2">
						<Label>{t('bulkActions.updateQuantity.newQuantity')}</Label>
						<Input
							type="number"
							min="1"
							step="1"
							value={quantityInput}
							onChange={(e) => setQuantityInput(e.target.value)}
							placeholder="1"
						/>
					</div>
					<DialogFooter>
						<Button onClick={() => setIsOpen(false)} variant="outline">
							{t('bulkActions.updateQuantity.cancel')}
						</Button>
						<Button onClick={handleSubmit} disabled={isPending || !isValid}>
							{isPending && <Loader2 className="size-4 animate-spin mr-2" />}
							{t('bulkActions.updateQuantity.update')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	)
}
